/**
 * trialLoop.js - ONE trial, and the only implementation of it.
 *
 * =============================================================================
 * WHY THIS FILE EXISTS
 *
 * A trial is the unit that produces every measurement on this platform. It was
 * written three times - once in each game, and a third time in the test harness
 * because the React version cannot run under `node --test`. Three
 * implementations of the sequence that produces every number a therapist reads
 * is three chances for the same box to mean three different things, which is
 * the exact failure section 9.1 of the spec exists to prevent.
 *
 * So the sequence lives here once, and the two things that genuinely differ -
 * how time passes, and how a child's voice arrives - are behind a STAGE.
 *
 * =============================================================================
 * ONE TRIAL, IN ORDER
 *
 *   open      stamp trialStartedAt, open the audio window, build a recorder
 *   prompt    the game speaks at the opening rung; stamp promptEndedAt when
 *             the speaker actually falls silent, not when the prompt was asked for
 *   listen    the ladder walks itself: clue -> more support -> the model,
 *             while the stage delivers whatever the child does
 *   close     on the first non-echo final result, or when the window expires
 *   judge     the game judges its own boxes; the engine judges the rest from
 *             the record, server-side
 *   post      one attempt, whole or not at all
 *
 * The loop never decides pacing, never colours a box, never numbers an attempt
 * and never fades a prompt. It produces a record and hands it over.
 *
 * =============================================================================
 * THE STAGE - the only thing a browser and a test disagree about
 *
 *   openTrial({ phase, level, target, windowMs })  a trial is starting
 *   now()                                         ms on one monotonic clock
 *   say(text, { models })                         speak, and RESOLVE when the
 *                                                 speaker has actually stopped
 *   show(patch)                                   optional; paint something
 *   nextEvent({ until, state })                   the heart of it - below
 *   audio({ startedAt, endedAt })                 the audio block, or UNMONITORED
 *   allowInferredOnset()                          optional; see trialRecorder
 *   onsetAccepted()                               optional; an onset was real
 *   noteModel(phrase, at)                         optional; for priorModelAgoMs
 *   post(payload)                                 optional; returns the response
 *   closeTrial({ endedAt, reason })               optional
 *   aborted()                                     optional; the level ended
 *
 * `nextEvent({ until })` resolves with the first thing that happens, or with a
 * `due` event when the clock reaches `until` and nothing has:
 *
 *   { kind: 'due',       at }
 *   { kind: 'onset',     at }
 *   { kind: 'utterance', at, transcript, confidence, alternatives }
 *   { kind: 'abort',     at }
 *
 * In a browser that is timers plus the recogniser's callbacks. In a test it is
 * a virtual clock plus a scripted child. Nothing else about a trial differs,
 * which is the point.
 *
 * =============================================================================
 * THE GAME - what the loop has to ask, and nothing more
 *
 *   id, capabilities                the declaration
 *   phraseFor(target)               what the child is being asked to say
 *   prompt({ phase, level, rungId, target, content })
 *                                   -> { text, cue, chips, showsAnswer }
 *   hint({ phase, level, target, content })            -> text
 *   judge({ phase, level, target, trial, transcript, alternatives })
 *                                   -> { components, success, expected, heard, detail }
 *   optionsVisible(phase, rungId)   were word frames on screen?
 *   onScreen(target)                every object the child could legally name
 *   gridState(target)               optional; the field, for later re-scoring
 *   targetPosition(target)          optional; where it was
 *
 * Everything else about a level - the boxes, the rungs, the strictness, the
 * pacing rules - the loop reads from the engine, so no game can answer it
 * differently.
 */

import { createTrialRecorder, slimAudio } from './trialRecorder.js';
import {
  buildLadderPlan, planModelsTarget, clampRung, rungIdAt, rungGivesTheAnswer,
  FULL_MODEL,
} from './support.js';
import { validateTrialRecord, MIN_VOCALISATION_MS } from '../../../engine/trial.js';
import { assessRecognition } from '../../../engine/scoring.js';
import { checkComponents, requiredCapsFor } from '../../../engine/contract.js';

/** What ended the trial. */
export const CLOSED_BY = Object.freeze({
  UTTERANCE: 'utterance',
  TIMEOUT: 'timeout',
});

/**
 * Play one trial.
 *
 * @param {object} input
 * @param {object} input.stage
 * @param {object} input.game
 * @param {number} input.phase
 * @param {number} input.level
 * @param {object} input.content       the game's own level entry; the loop reads
 *        only `nominalRung` from it and passes the rest back to the game
 * @param {object} input.target        whatever the game's planner produced
 * @param {{hintAtMs:number, escalateAtMs:number[], windowMs:number}} input.pacing
 * @param {number} input.openingIndex  ladder index the trial opens at
 * @param {number} [input.attemptNumber=1]  the game's own count of its patience.
 *        It goes into the trial record and is NOT asserted in the request: the
 *        server derives what the chart uses (ENGINE-BUILD.md section 11.3).
 * @param {number} [input.step=1]
 * @param {number} [input.supportIncreases=0]
 * @param {number|null} [input.priorModelAgoMs]
 * @returns {Promise<object>} the trial, or `{ cancelled: true }`
 */
export async function runTrial({
  stage,
  game,
  phase,
  level,
  content,
  target,
  pacing,
  openingIndex,
  attemptNumber = 1,
  step = 1,
  supportIncreases = 0,
  priorModelAgoMs = null,
}) {
  const capabilities = game.capabilities;
  const openingRungId = rungIdAt(clampRung(phase, openingIndex));
  const phrase = game.phraseFor(target);

  // The plan is built BEFORE the record, because whether this trial would ever
  // model the target is itself evidence: on a trial where the answer was never
  // on offer, "produced it without echoing" is free, and the engine excludes
  // the box rather than scoring it (ENGINE-BUILD.md section 11.2).
  const plan = buildLadderPlan({ phase, openingIndex: clampRung(phase, openingIndex), pacing });

  // Every trial takes a token: a loop that wakes from an await into a trial
  // that has been replaced must stop, not carry on writing a record nobody is
  // playing.
  const token = stage.openTrial({ phase, level, target, windowMs: pacing.windowMs });
  const startedAt = stage.now();

  const recorder = createTrialRecorder({
    phase,
    level,
    attemptNumber,
    nominalRungId: openingRungId,
    startedAt,
    responseWindowMs: pacing.windowMs,
    targetWord: phrase,
    targetObject: target.objectId ?? null,
    targetPosition: game.targetPosition ? game.targetPosition(target) : null,
    legalMoves: game.onScreen ? game.onScreen(target) : [],
    gridState: game.gridState ? game.gridState(target) : null,
    optionsVisible: game.optionsVisible ? game.optionsVisible(phase, openingRungId) : false,
    modelScheduled: planModelsTarget(plan, openingRungId),
    priorModelAgoMs,
  });

  const aborted = () => Boolean(stage.aborted && stage.aborted(token));

  /**
   * The game speaks, and the trial is told exactly when.
   *
   * The window is opened BEFORE the utterance, not after: a result that arrives
   * mid-sentence has to be testable against what was being said, or the game's
   * own voice comes back through the live microphone and is scored as the
   * child's answer (audit finding B42).
   */
  async function speak(text, models) {
    if (!text) return null;
    const from = stage.now();
    recorder.pipSpeaking(text, from);
    let to = from;
    try {
      await stage.say(text, { models });
    } finally {
      to = stage.now();
      // Closed in `finally`, so a synthesiser that throws still closes the
      // window it opened - an open window swallows every later vocalisation.
      recorder.pipSpoke(text, from, to);
      if (models && stage.noteModel) stage.noteModel(models, to);
    }
    return { from, to };
  }

  // ---- prompt -------------------------------------------------------------
  const opening = game.prompt({ phase, level, rungId: openingRungId, target, content });
  if (stage.show) {
    stage.show({ kind: 'prompt', phase, level, target, rungId: openingRungId, prompt: opening });
  }
  const spokenPrompt = await speak(opening.text, opening.showsAnswer ? phrase : null);
  if (aborted()) return { cancelled: true };
  // Section 8.2: at a rung that hands the answer over, every judgement asking
  // "did they need it" turns on this stamp.
  if (opening.showsAnswer && spokenPrompt) recorder.modelEnded(spokenPrompt.to);

  const promptEndedAt = stage.now();
  recorder.promptEnded(promptEndedAt);
  if (stage.show) stage.show({ kind: 'listening', phase, level, target });

  /**
   * One step down the ladder, mid-trial.
   *
   * This is the signal Phase 2 A/B/C are computed from: a child who answered
   * before any of it fired did something different from one who answered after
   * the word was finally said, and only the record knows which.
   */
  async function walk(current) {
    if (current.kind === 'hint') {
      recorder.hintFired(current.at);
      const text = game.hint({ phase, level, target, content });
      if (stage.show) stage.show({ kind: 'hint', phase, level, target, text });
      await speak(text, null);
      return;
    }

    if (current.kind === 'support') recorder.supportChanged(current.rungId, current.at);

    // The last step of any ladder models the target - errorless learning does
    // not leave a child with no answer - and that can happen at a rung well
    // above `full_model`, because a phase's bounds may not reach it. The rung
    // stays what it is; `modelPlayed` records what was done. Two different
    // facts, two different fields (ENGINE-BUILD.md section 11.5).
    const templateRung = current.models ? FULL_MODEL : current.rungId;
    const prompt = game.prompt({ phase, level, rungId: templateRung, target, content });
    if (stage.show) {
      stage.show({
        kind: 'ladder', phase, level, target, rungId: current.rungId, prompt, step: current,
      });
    }
    const spoken = await speak(prompt.text, prompt.showsAnswer ? phrase : null);
    if (prompt.showsAnswer && spoken) recorder.modelEnded(spoken.to);
  }

  // ---- listen -------------------------------------------------------------
  const steps = plan
    .map((s) => ({ ...s, at: promptEndedAt + s.atMs }))
    .sort((a, b) => a.at - b.at);
  const windowEndsAt = promptEndedAt + pacing.windowMs;

  let next = 0;
  let endedAt = windowEndsAt;
  let reason = CLOSED_BY.TIMEOUT;

  for (;;) {
    const dueAt = next < steps.length ? Math.min(steps[next].at, windowEndsAt) : windowEndsAt;
    const event = await stage.nextEvent({ until: dueAt, state: recorder.state(), token });
    if (!event || event.kind === 'abort' || aborted()) return { cancelled: true };

    if (event.kind === 'onset') {
      // Refused if it falls inside a window where the game itself was talking.
      if (recorder.speechStarted(event.at) && stage.onsetAccepted) stage.onsetAccepted();
      if (stage.now() < windowEndsAt) continue;
      break;
    }

    if (event.kind === 'utterance') {
      if (recorder.isEcho(event.at, event.transcript)) {
        // The game's own voice, coming back. Audit B42/B43: this is the failure
        // that lets a child pass a level by echoing the question. Dropped, and
        // kept in diagnostics so it stays visible.
        recorder.echoDropped({ transcript: event.transcript, at: event.at });
        if (stage.now() < windowEndsAt) continue;
        break;
      }
      recorder.utterance({
        transcript: event.transcript,
        confidence: event.confidence === undefined ? null : event.confidence,
        alternatives: event.alternatives || [],
        at: event.at,
      });
      endedAt = event.at;
      reason = CLOSED_BY.UTTERANCE;
      break;
    }

    // 'due': either the next ladder step, or the end of the response window.
    if (next < steps.length && event.at >= steps[next].at) {
      const current = steps[next];
      next += 1;
      await walk(current);
      if (aborted()) return { cancelled: true };
      continue;
    }
    endedAt = Math.max(event.at, windowEndsAt);
    break;
  }

  // ---- close, judge, post -------------------------------------------------
  // A trial cannot end before the present moment: a model that ran long is
  // still part of the trial it ran in.
  endedAt = Math.max(endedAt, stage.now());

  const audio = stage.audio({ startedAt, endedAt });
  const raw = recorder.build({
    endedAt,
    // The RMS envelope is a debugging aid and nothing judges from it; it is
    // kept locally and dropped on the way out.
    audio: slimAudio(audio),
    allowInferredOnset: Boolean(stage.allowInferredOnset && stage.allowInferredOnset()),
  });

  const validated = validateTrialRecord(raw, {
    requires: requiredCapsFor(phase, level, capabilities),
  });
  const record = validated.normalised;

  const judged = game.judge({
    phase,
    level,
    target,
    trial: record,
    transcript: raw.transcript,
    alternatives: raw.alternatives,
  });

  // The engine's confidence gate, run here so a game can tell the difference
  // between "they got it wrong" and "we did not hear them". An unreliable
  // result is INCONCLUSIVE, never a failure (section 2.4).
  const heardWell = assessRecognition({
    phase,
    confidence: raw.confidence,
    transcript: raw.transcript,
    alternatives: raw.alternatives,
  });

  const check = checkComponents({ phase, level, capabilities, components: judged.components });

  // Section 2.5's input to the ENGINE's fade rule: did the child get there
  // without help beyond the rung the trial opened at? The engine decides what
  // that earns; no game has a fading rule of its own.
  const unaided = Boolean(
    record
    && !record.escalated
    && !record.hintFired
    && !(record.modelPlayed && rungGivesTheAnswer(record.nominalRung))
  );

  const qualifying = record && record.audio && record.audio.monitored
    ? (record.audio.vocalisations || []).filter((v) => v.durationMs >= MIN_VOCALISATION_MS).length
    : 0;

  const payload = {
    gameId: game.id,
    phase,
    level,
    step,
    expected: judged.expected,
    heard: judged.heard,
    rawTranscript: raw.transcript,
    components: judged.components,
    trial: raw,
    // The rung live at speech onset. `targetPromptLevel` is the rung this LEVEL
    // is designed to sit at - per-level content, which the server cannot know,
    // and the ceiling the engine fades toward.
    promptLevel: record ? record.supportRung : openingRungId,
    targetPromptLevel: content.nominalRung,
    recognition: { confidence: raw.confidence, alternatives: raw.alternatives },
    supportIncreases,
    // Deliberately absent: attemptNumber. Section 7.4 colours a box from FIRST
    // attempts, so the field that decides which plays reach the chart at all is
    // derived from the stored history and never asserted by a game.
  };

  let posted = null;
  const ok = validated.ok && check.ok;
  if (ok && stage.post) posted = await stage.post(payload);

  if (stage.closeTrial) stage.closeTrial({ endedAt, reason, token });

  return {
    ok,
    cancelled: false,
    reason,
    startedAt,
    endedAt,
    raw,
    record,
    validated,
    check,
    judged,
    heardWell,
    unaided,
    qualifying,
    payload,
    posted,
    diagnostics: recorder.diagnostics(),
    // The whole-attempt outcome, which is a different thing from a box: "did
    // the child complete the step". It is what section 8.4's windows read.
    outcome: { success: judged.success, counted: heardWell.reliable, unaided },
  };
}

export default { runTrial, CLOSED_BY };
